import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import type { Lead, LeadFormData, LeadFilters } from '@/types/crm';

interface LeadStore {
  leads: Lead[];
  selectedLead: Lead | null;
  isLoading: boolean;
  filters: LeadFilters;
  fetchLeads: () => Promise<void>;
  fetchLeadById: (id: string) => Promise<Lead | null>;
  createLead: (data: LeadFormData) => Promise<Lead | null>;
  updateLead: (id: string, data: Partial<LeadFormData>) => Promise<void>;
  deleteLead: (id: string) => Promise<void>;
  convertLead: (id: string) => Promise<{ company_id: string; contact_id: string; deal_id: string } | null>;
  setSelectedLead: (lead: Lead | null) => void;
  setFilters: (filters: LeadFilters) => void;
}

export const useLeadStore = create<LeadStore>((set, get) => ({
  leads: [],
  selectedLead: null,
  isLoading: false,
  filters: {},

  fetchLeads: async () => {
    set({ isLoading: true });
    try {
      const { filters } = get();
      let query = supabase
        .from('crm_leads_with_details')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.source) {
        query = query.eq('source', filters.source);
      }
      if (filters.priority) {
        query = query.eq('priority', filters.priority);
      }
      if (filters.assigned_to) {
        query = query.eq('assigned_to', filters.assigned_to);
      }
      if (filters.date_from) {
        query = query.gte('created_at', filters.date_from);
      }
      if (filters.date_to) {
        query = query.lte('created_at', filters.date_to);
      }
      if (filters.search) {
        query = query.or(`company_name.ilike.%${filters.search}%,first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      set({ leads: data || [] });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchLeadById: async (id: string) => {
    const { data, error } = await supabase
      .from('crm_leads_with_details')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) return null;
    set({ selectedLead: data });
    return data;
  },

  createLead: async (data: LeadFormData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: lead, error } = await supabase
      .from('crm_leads')
      .insert({
        ...data,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    await get().fetchLeads();
    return lead;
  },

  updateLead: async (id: string, data: Partial<LeadFormData>) => {
    const { error } = await supabase
      .from('crm_leads')
      .update(data)
      .eq('id', id);

    if (error) throw error;
    await get().fetchLeads();
  },

  deleteLead: async (id: string) => {
    const { error } = await supabase
      .from('crm_leads')
      .delete()
      .eq('id', id);

    if (error) throw error;
    await get().fetchLeads();
  },

  convertLead: async (id: string) => {
    // Pre-check: re-fetch the lead to ensure it hasn't already been converted
    const { data: freshLead } = await supabase
      .from('crm_leads')
      .select('status, converted_to_deal_id')
      .eq('id', id)
      .single();

    if (freshLead?.converted_to_deal_id || freshLead?.status === 'converted') {
      await get().fetchLeads();
      throw new Error('This lead has already been converted.');
    }

    const { data, error } = await supabase.rpc('crm_convert_lead', { p_lead_id: id });
    if (error) {
      // Refresh leads to sync UI with current DB state
      await get().fetchLeads();
      throw error;
    }
    await get().fetchLeads();
    return data;
  },

  setSelectedLead: (lead: Lead | null) => set({ selectedLead: lead }),
  setFilters: (filters: LeadFilters) => set({ filters }),
}));
